import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Package, ShoppingCart, Users, Plus, TrendingUp, Boxes,
  UserPlus, DollarSign, Percent, LogOut, X, Check, QrCode,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api, { money, fmtErr } from "@/lib/api";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const NAV = [
  ["overview", "Dashboard", LayoutDashboard],
  ["campaigns", "Campaigns", Package],
  ["orders", "Orders", ShoppingCart],
  ["customers", "Customers", Users],
  ["redeem", "Redeem", QrCode],
];
const PIE = ["#FF5A36", "#00B884", "#4F46E5", "#FFC72C", "#EF4444", "#8B5E34", "#6366F1"];

export default function Merchant() {
  const { user, checking, logout } = useAuth();
  const [tab, setTab] = useState("overview");

  if (checking || user === null) return <div className="min-h-screen grid place-items-center text-stone-400">Loading…</div>;
  if (!user || (user.role !== "merchant" && user.role !== "admin")) {
    return (
      <div className="min-h-screen grid place-items-center px-4">
        <div className="max-w-sm text-center">
          <div className="text-3xl mb-4"><Logo /></div>
          <h1 className="font-display text-2xl font-extrabold text-stone-900 mb-2">Merchant Portal</h1>
          <p className="text-stone-500 mb-6">Sign in with a merchant account to manage campaigns and analytics.</p>
          <Link to="/login" className="px-6 py-3 rounded-full font-bold text-white inline-block" style={{ background: "var(--friib)" }}>Sign in</Link>
          <p className="mt-4 text-xs text-stone-400">Demo: merchant@friib.com / merchant123</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex" style={{ marginBottom: "-4rem" }}>
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-60 shrink-0 text-white p-5" style={{ background: "var(--ink)" }}>
        <Link to="/" className="text-2xl mb-8"><Logo onDark /></Link>
        <nav className="space-y-1 flex-1">
          {NAV.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`merchant-nav-${k}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors ${tab === k ? "bg-white/10 text-white" : "text-stone-400 hover:text-white"}`}>
              <Icon size={18} /> {l}
            </button>
          ))}
        </nav>
        <button onClick={logout} className="flex items-center gap-3 px-4 py-2.5 text-stone-400 hover:text-white text-sm font-semibold"><LogOut size={18} /> Sign out</button>
      </aside>

      <div className="flex-1 min-w-0">
        {/* Mobile tabs */}
        <div className="lg:hidden flex gap-2 overflow-x-auto no-scrollbar p-3 bg-white border-b border-stone-200 sticky top-0 z-10">
          {NAV.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-bold ${tab === k ? "text-white" : "bg-stone-100 text-stone-600"}`} style={{ background: tab === k ? "var(--friib)" : undefined }}><Icon size={15} /> {l}</button>
          ))}
        </div>
        <div className="p-4 sm:p-8">
          {tab === "overview" && <Overview />}
          {tab === "campaigns" && <Campaigns />}
          {tab === "orders" && <Orders />}
          {tab === "customers" && <Customers />}
          {tab === "redeem" && <Redeem />}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Overview ---------------- */
function Overview() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/merchant/analytics").then((r) => setA(r.data)); }, []);
  if (!a) return <div className="text-stone-400">Loading analytics…</div>;
  const k = a.kpis;
  const kpis = [
    { label: "Inventory Moved", value: k.inventory_moved.toLocaleString(), sub: "units", icon: Boxes, color: "#FF5A36" },
    { label: "New Customers", value: k.new_customers.toLocaleString(), sub: "acquired", icon: UserPlus, color: "#00B884" },
    { label: "Upsell Revenue", value: money(k.upsell_revenue), sub: "immediate", icon: DollarSign, color: "#4F46E5" },
    { label: "Upsell Conversion", value: `${k.upsell_conversion}%`, sub: "of claims", icon: Percent, color: "#FFC72C" },
    { label: "Revenue / Friib", value: money(k.revenue_per_friib), sub: "per unit given", icon: TrendingUp, color: "#EF4444" },
    { label: "Est. CAC", value: money(k.cac), sub: "per customer", icon: Users, color: "#6366F1" },
  ];
  return (
    <div>
      <h1 className="font-display text-2xl lg:text-3xl font-extrabold text-stone-900 mb-1">Campaign Performance</h1>
      <p className="text-stone-500 mb-6">Friib turns unsold inventory into measurable customer acquisition.</p>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {kpis.map((kp, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="rounded-2xl bg-white border border-stone-100 friib-shadow p-5" data-testid={`kpi-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="w-9 h-9 grid place-items-center rounded-full" style={{ background: `${kp.color}18` }}><kp.icon size={18} style={{ color: kp.color }} /></span>
            </div>
            <p className="font-display text-2xl lg:text-3xl font-extrabold text-stone-900">{kp.value}</p>
            <p className="text-xs font-semibold text-stone-500">{kp.label} <span className="text-stone-400 font-normal">· {kp.sub}</span></p>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-8">
        <div className="rounded-2xl bg-white border border-stone-100 friib-shadow p-5" data-testid="chart-claims">
          <h3 className="font-display font-bold text-stone-900 mb-4">Friibs claimed (30 days)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={a.claims_series}>
              <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#FF5A36" stopOpacity={0.35} /><stop offset="95%" stopColor="#FF5A36" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1efec" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#a8a29e" }} interval={5} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={28} />
              <Tooltip />
              <Area type="monotone" dataKey="value" stroke="#FF5A36" strokeWidth={2.5} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl bg-white border border-stone-100 friib-shadow p-5" data-testid="chart-revenue">
          <h3 className="font-display font-bold text-stone-900 mb-4">Revenue generated (30 days)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={a.revenue_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1efec" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#a8a29e" }} interval={5} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={34} />
              <Tooltip />
              <Bar dataKey="value" fill="#00B884" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="rounded-2xl bg-white border border-stone-100 friib-shadow p-5" data-testid="chart-category">
          <h3 className="font-display font-bold text-stone-900 mb-4">Inventory by category</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={a.category_data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={3}>
                {a.category_data.map((e, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 mt-2">{a.category_data.map((c, i) => <span key={i} className="text-xs text-stone-500 flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE[i % PIE.length] }} />{c.name}</span>)}</div>
        </div>
        <div className="lg:col-span-2 rounded-2xl bg-white border border-stone-100 friib-shadow p-5 overflow-x-auto" data-testid="table-campaigns">
          <h3 className="font-display font-bold text-stone-900 mb-4">Top campaigns</h3>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-stone-400 border-b border-stone-100"><th className="pb-2 font-semibold">Campaign</th><th className="pb-2 font-semibold">Claimed</th><th className="pb-2 font-semibold">Conv.</th><th className="pb-2 font-semibold">Upsell Rev.</th></tr></thead>
            <tbody>
              {a.campaigns.map((c) => (
                <tr key={c.slug} className="border-b border-stone-50">
                  <td className="py-3 flex items-center gap-2"><img src={c.image} alt="" className="w-8 h-8 rounded-lg object-cover" /><span className="font-semibold text-stone-800">{c.name}</span></td>
                  <td className="py-3 text-stone-600">{c.claimed}/{c.total}</td>
                  <td className="py-3"><span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">{c.conversion}%</span></td>
                  <td className="py-3 font-semibold text-stone-800">{money(c.upsell_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Campaigns + Wizard ---------------- */
function Campaigns() {
  const [list, setList] = useState([]);
  const [wizard, setWizard] = useState(false);
  const load = () => api.get("/merchant/campaigns").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl lg:text-3xl font-extrabold text-stone-900">Your Campaigns</h1>
        <button onClick={() => setWizard(true)} data-testid="new-campaign-btn" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full font-bold text-white" style={{ background: "var(--friib)" }}><Plus size={18} /> Create a Friib</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((c) => (
          <div key={c.slug} className="rounded-2xl bg-white border border-stone-100 friib-shadow overflow-hidden" data-testid={`campaign-${c.slug}`}>
            <img src={c.images[0]} alt="" className="w-full h-36 object-cover" />
            <div className="p-4">
              <p className="font-display font-bold text-stone-900">{c.name}</p>
              <p className="text-sm text-stone-400 mb-2">{money(c.retail_value)} value · {money(c.shipping)} S&H</p>
              <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden"><div className="h-full" style={{ width: `${c.claimed_pct}%`, background: "var(--mint)" }} /></div>
              <p className="text-xs text-stone-500 mt-1">{c.inventory_claimed}/{c.inventory_total} claimed</p>
            </div>
          </div>
        ))}
      </div>
      {wizard && <Wizard onClose={() => setWizard(false)} onDone={() => { setWizard(false); load(); }} />}
    </div>
  );
}

function Wizard({ onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", category: "beauty", image: "", retail_value: "", inventory_total: "", shipping: "", description: "", upsell_name: "", upsell_regular_price: "", upsell_price: "" });
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/merchant/campaigns", {
        name: f.name, category: f.category, retail_value: +f.retail_value, shipping: +f.shipping,
        inventory_total: +f.inventory_total, description: f.description, image: f.image || "https://images.unsplash.com/photo-1743309026555-97f545a08490?w=800",
        upsell_name: f.upsell_name || null, upsell_regular_price: f.upsell_regular_price ? +f.upsell_regular_price : null, upsell_price: f.upsell_price ? +f.upsell_price : null,
      });
      toast.success("Campaign published! 🚀"); onDone();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); setBusy(false); }
  };

  const steps = ["Product", "Image & value", "Inventory & shipping", "Upsell", "Publish"];
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="campaign-wizard">
      <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-stone-100">
          <div><p className="text-xs font-bold uppercase tracking-wider text-[var(--friib)]">Step {step} of 5</p><h3 className="font-display font-bold text-lg text-stone-900">{steps[step - 1]}</h3></div>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-3 min-h-[220px]">
          {step === 1 && <>
            <WField label="Product name" value={f.name} onChange={set("name")} tid="wiz-name" />
            <label className="block"><span className="text-xs font-semibold text-stone-500">Category</span>
              <select value={f.category} onChange={set("category")} data-testid="wiz-category" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-stone-200">{cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}</select></label>
            <WField label="Short description" value={f.description} onChange={set("description")} tid="wiz-desc" />
          </>}
          {step === 2 && <>
            <WField label="Product image URL" value={f.image} onChange={set("image")} tid="wiz-image" placeholder="https://…" />
            <WField label="Retail value ($)" type="number" value={f.retail_value} onChange={set("retail_value")} tid="wiz-value" />
          </>}
          {step === 3 && <>
            <WField label="Quantity available" type="number" value={f.inventory_total} onChange={set("inventory_total")} tid="wiz-inventory" />
            <WField label="Shipping & handling ($)" type="number" value={f.shipping} onChange={set("shipping")} tid="wiz-shipping" />
          </>}
          {step === 4 && <>
            <p className="text-sm text-stone-500">Add a one-click post-purchase upsell (optional).</p>
            <WField label="Upsell product name" value={f.upsell_name} onChange={set("upsell_name")} tid="wiz-upsell-name" />
            <div className="grid grid-cols-2 gap-3">
              <WField label="Regular price ($)" type="number" value={f.upsell_regular_price} onChange={set("upsell_regular_price")} tid="wiz-upsell-reg" />
              <WField label="Upsell price ($)" type="number" value={f.upsell_price} onChange={set("upsell_price")} tid="wiz-upsell-price" />
            </div>
          </>}
          {step === 5 && <div className="text-center py-4">
            {f.image && <img src={f.image} alt="" className="w-28 h-28 object-cover rounded-2xl mx-auto mb-3" />}
            <p className="font-display font-bold text-xl text-stone-900">{f.name || "Your Friib"}</p>
            <p className="text-stone-500">{money(f.retail_value || 0)} value · {money(f.shipping || 0)} shipping · {f.inventory_total || 0} units</p>
            {f.upsell_name && <p className="text-sm text-[var(--friib)] mt-1">Upsell: {f.upsell_name} @ {money(f.upsell_price || 0)}</p>}
          </div>}
        </div>
        <div className="flex justify-between p-5 border-t border-stone-100">
          <button onClick={() => (step === 1 ? onClose() : setStep(step - 1))} className="px-5 py-2.5 rounded-full font-bold text-stone-600 bg-stone-100">{step === 1 ? "Cancel" : "Back"}</button>
          {step < 5 ? <button onClick={() => setStep(step + 1)} data-testid="wiz-next" className="px-6 py-2.5 rounded-full font-bold text-white" style={{ background: "var(--ink)" }}>Next</button>
            : <button onClick={submit} disabled={busy} data-testid="wiz-publish" className="px-6 py-2.5 rounded-full font-bold text-white flex items-center gap-1.5 disabled:opacity-60" style={{ background: "var(--friib)" }}><Check size={16} /> Publish</button>}
        </div>
      </div>
    </div>
  );
}
function WField({ label, value, onChange, type = "text", placeholder, tid }) {
  return <label className="block"><span className="text-xs font-semibold text-stone-500">{label}</span><input type={type} value={value} onChange={onChange} placeholder={placeholder} data-testid={tid} className="mt-1 w-full px-4 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-[var(--friib)]" /></label>;
}

/* ---------------- Orders ---------------- */
function Orders() {
  const [d, setD] = useState({ orders: [], passes: [] });
  useEffect(() => { api.get("/merchant/orders").then((r) => setD(r.data)); }, []);
  return (
    <div>
      <h1 className="font-display text-2xl lg:text-3xl font-extrabold text-stone-900 mb-6">Orders & Claims</h1>
      <div className="rounded-2xl bg-white border border-stone-100 friib-shadow overflow-x-auto">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead><tr className="text-left text-stone-400 border-b border-stone-100"><th className="p-4 font-semibold">Customer</th><th className="p-4 font-semibold">Friib</th><th className="p-4 font-semibold">Add-ons</th><th className="p-4 font-semibold">Total</th><th className="p-4 font-semibold">Status</th></tr></thead>
          <tbody>
            {d.orders.map((o) => (
              <tr key={o.id} className="border-b border-stone-50"><td className="p-4 font-semibold text-stone-800">{o.customer_name}<br /><span className="text-xs text-stone-400 font-normal">{o.email}</span></td><td className="p-4 text-stone-600">{o.product_name}</td><td className="p-4 text-stone-600">{o.upsells?.length || 0}</td><td className="p-4 font-semibold">{money(o.total)}</td><td className="p-4"><span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 capitalize">{o.status}</span></td></tr>
            ))}
            {d.passes.map((p) => (
              <tr key={p.id} className="border-b border-stone-50"><td className="p-4 font-semibold text-stone-800">{p.customer_name}<br /><span className="text-xs text-stone-400 font-normal">{p.email}</span></td><td className="p-4 text-stone-600">{p.offer_title} <span className="text-xs text-[var(--mint)]">(Local)</span></td><td className="p-4 text-stone-600">—</td><td className="p-4 font-semibold">{p.code}</td><td className="p-4"><span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${p.status === "used" ? "bg-stone-100 text-stone-600" : "bg-amber-100 text-amber-700"}`}>{p.status}</span></td></tr>
            ))}
            {d.orders.length === 0 && d.passes.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-stone-400">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Customers ---------------- */
function Customers() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/merchant/customers").then((r) => setList(r.data)); }, []);
  const exportCsv = () => {
    const rows = [["Name", "Email", "Claimed", "Upsells", "LTV"], ...list.map((c) => [c.name, c.email, c.claimed.join("|"), c.upsells, c.ltv])];
    const csv = rows.map((r) => r.map((x) => `"${x}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "friib-customers.csv"; a.click();
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl lg:text-3xl font-extrabold text-stone-900">Customers Acquired</h1>
        <button onClick={exportCsv} data-testid="export-csv-btn" className="px-5 py-2.5 rounded-full font-bold text-stone-900 bg-white friib-shadow border border-stone-200">Export CSV</button>
      </div>
      <div className="rounded-2xl bg-white border border-stone-100 friib-shadow overflow-x-auto">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead><tr className="text-left text-stone-400 border-b border-stone-100"><th className="p-4 font-semibold">Customer</th><th className="p-4 font-semibold">Claimed</th><th className="p-4 font-semibold">Upsells</th><th className="p-4 font-semibold">LTV</th></tr></thead>
          <tbody>
            {list.map((c, i) => (
              <tr key={i} className="border-b border-stone-50"><td className="p-4 font-semibold text-stone-800">{c.name}<br /><span className="text-xs text-stone-400 font-normal">{c.email}</span></td><td className="p-4 text-stone-600">{c.claimed.join(", ")}</td><td className="p-4 text-stone-600">{c.upsells}</td><td className="p-4 font-semibold">{money(c.ltv)}</td></tr>
            ))}
            {list.length === 0 && <tr><td colSpan={4} className="p-10 text-center text-stone-400">No customers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Redeem ---------------- */
function Redeem() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const redeem = async (e) => {
    e.preventDefault();
    // find pass by code via customers/orders is not exposed; use passes lookup by scanning merchant orders
    try {
      const { data } = await api.get("/merchant/orders");
      const p = data.passes.find((x) => x.code.toUpperCase() === code.trim().toUpperCase());
      if (!p) { toast.error("No matching Friib pass found."); return; }
      await api.post(`/passes/${p.id}/redeem`);
      setResult({ ok: true, name: p.customer_name, title: p.offer_title });
      toast.success("Friib redeemed!");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };
  return (
    <div className="max-w-md">
      <h1 className="font-display text-2xl lg:text-3xl font-extrabold text-stone-900 mb-2">Redeem a Local Friib</h1>
      <p className="text-stone-500 mb-6">Enter the customer's Friib code to mark it as used.</p>
      <form onSubmit={redeem} className="flex gap-2" data-testid="redeem-form">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="FRB-XXXXXXXX" data-testid="redeem-code" className="flex-1 px-4 py-3 rounded-xl border border-stone-200 font-mono outline-none focus:border-[var(--mint)]" />
        <button className="px-6 py-3 rounded-xl font-bold text-white" style={{ background: "var(--mint)" }}>Redeem</button>
      </form>
      {result?.ok && <div className="mt-5 rounded-2xl bg-emerald-50 border border-emerald-200 p-5 flex items-center gap-3" data-testid="redeem-result"><Check className="text-emerald-600" /><div><p className="font-bold text-emerald-800">{result.title} redeemed</p><p className="text-sm text-emerald-700">for {result.name}</p></div></div>}
    </div>
  );
}
